import { useEffect, useState } from "react";
import { Star, AlertCircle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrandTag } from "@/components/BrandTag";
import { useUpdateStore } from "@/hooks/usePosStoreMutations";
import { useToast } from "@/hooks/use-toast";
import {
  DUPLICATE_LOCATION_MESSAGE,
  findDuplicateStore,
  isPlaceholderStoreId,
  validateRequiredField,
  validateStoreId,
  type PosStore,
} from "@/utils/posStores";

interface SelectedStoreBarProps {
  store: PosStore | null;
  /** The current registry, so a rename cannot collide with another store's
   *  (brand, location_name) pair -- the same guard AddStoreDialog applies. */
  stores?: readonly PosStore[];
  pinned: boolean;
  onTogglePin: () => void;
  statusMsg: string;
  statusError: boolean;
  /** Called with the fresh row after a successful edit, so the parent's
   *  selection reflects the new location name / store id immediately. */
  onStoreUpdated?: (store: PosStore) => void;
}

/**
 * The currently selected store, shown next to the orders table/summary
 * instead of buried in the picker card (captain's ask: "for ease of
 * viewing"). Sticky on phones, where the picker + pinned cards otherwise push
 * this off-screen while scrolling the order list; static at `lg` where the
 * whole page fits one screen. The Fetch button itself lives next to the
 * store search input in the picker card, not here.
 */
export function SelectedStoreBar({
  store,
  stores,
  pinned,
  onTogglePin,
  statusMsg,
  statusError,
  onStoreUpdated,
}: SelectedStoreBarProps) {
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Card className="sticky top-0 z-10 lg:static bg-card/95 lg:bg-card backdrop-blur lg:backdrop-blur-none">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {store ? (
              <>
                <BrandTag brand={store.brand} label={store.location_name} size="sm" className="font-semibold" />
                <span className="font-mono tabular-nums text-xs text-muted-foreground">
                  {store.store_id}
                </span>
                {store.id != null && (
                  <button
                    type="button"
                    aria-label="Edit location name or store ID"
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                    onClick={() => setEditOpen(true)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={onTogglePin}
                  aria-pressed={pinned}
                >
                  <Star
                    className={`w-3.5 h-3.5 mr-1 ${
                      pinned ? "fill-status-caution text-status-caution-foreground" : ""
                    }`}
                  />
                  {pinned ? "Pinned" : "Pin store"}
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                No store selected. Search above or tap a pinned store.
              </span>
            )}
            {statusMsg && (
              <span
                role="status"
                aria-live="polite"
                className={`flex items-center gap-1.5 text-sm ${
                  statusError ? "text-status-danger" : "text-muted-foreground"
                }`}
              >
                {statusError && <AlertCircle className="w-4 h-4 flex-none" />}
                <span>{statusMsg}</span>
              </span>
            )}
          </div>
        </div>
      </CardContent>
      {store && store.id != null && (
        <EditStoreDialog
          key={store.id}
          store={store}
          stores={stores}
          open={editOpen}
          onOpenChange={setEditOpen}
          onUpdated={onStoreUpdated}
        />
      )}
    </Card>
  );
}

export interface EditStoreDialogProps {
  store: PosStore;
  stores?: readonly PosStore[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated?: (store: PosStore) => void;
}

/** Minimal edit affordance: location name, plus the Adora store id while the
 *  store is still a placeholder (blank/NOID). Once a real id is set it is
 *  locked here, since pins and snapshots are keyed by it. Brand isn't
 *  editable here -- changing brand is really "add a new store" instead.
 *  Also opened directly from OrderSpy's "Add ID" affordance on an unavailable
 *  search row, since normal selection refuses placeholder stores. */
export function EditStoreDialog({
  store,
  stores = [],
  open,
  onOpenChange,
  onUpdated,
}: EditStoreDialogProps) {
  const [locationName, setLocationName] = useState(store.location_name);
  const [storeId, setStoreId] = useState(store.store_id);
  const [submitted, setSubmitted] = useState(false);
  const updateStore = useUpdateStore();
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    setLocationName(store.location_name);
    setStoreId(store.store_id);
    setSubmitted(false);
    updateStore.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, store.id, store.location_name, store.store_id]);

  const storeIdLocked = !isPlaceholderStoreId(store.store_id);
  const validateLocation = () =>
    validateRequiredField(locationName, "Location name") ??
    (findDuplicateStore(
      stores.filter((s) => s.id !== store.id),
      store.brand,
      locationName,
    )
      ? DUPLICATE_LOCATION_MESSAGE
      : null);
  const locationError = submitted ? validateLocation() : null;
  const storeIdError = submitted && !storeIdLocked ? validateStoreId(storeId) : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (
      validateLocation() ||
      (!storeIdLocked && validateStoreId(storeId)) ||
      store.id == null
    ) {
      return;
    }

    updateStore.mutate(
      storeIdLocked
        ? { id: store.id, location_name: locationName }
        : { id: store.id, location_name: locationName, store_id: storeId },
      {
        onSuccess: (updated) => {
          toast({ title: "Store updated", description: `${updated.brand}: ${updated.location_name}` });
          onUpdated?.(updated);
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: "Couldn't update store",
            description: error instanceof Error ? error.message : String(error),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {store.brand}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit-store-location">Location name</Label>
            <Input
              id="edit-store-location"
              className="h-11 text-base mt-1.5"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />
            {locationError && <p className="text-sm text-status-danger mt-1">{locationError}</p>}
          </div>
          <div>
            <Label htmlFor="edit-store-id">Adora store ID</Label>
            <Input
              id="edit-store-id"
              className="h-11 text-base mt-1.5 font-mono"
              value={storeId}
              placeholder="5 letters/numbers, or blank if not on Adora yet"
              onChange={(e) => setStoreId(e.target.value)}
              disabled={storeIdLocked}
              readOnly={storeIdLocked}
              aria-describedby={storeIdLocked ? "edit-store-id-locked" : undefined}
            />
            {storeIdLocked && (
              <p id="edit-store-id-locked" className="text-sm text-muted-foreground mt-1">
                Store ID can only be set once, while the store is not yet on Adora. Pins and
                snapshot history are keyed by it.
              </p>
            )}
            {storeIdError && <p className="text-sm text-status-danger mt-1">{storeIdError}</p>}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={updateStore.isPending} className="h-11">
              {updateStore.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SelectedStoreBar;
